
import React from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Navigate } from 'react-router-dom';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: 'superadmin' | 'administrator';
  requireAuth?: boolean;
  redirectTo?: string;
}

export const ProtectedRoute = ({ 
  children, 
  requiredRole, 
  requireAuth = false,
  redirectTo = '/auth' 
}: ProtectedRouteProps) => {
  const { profile, loading, isImpersonating, session, effectiveRole } = useAuth();

  // CRITICAL: Wait for auth to fully initialize before making redirect decisions
  // This prevents redirects during page reload while session is being restored
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  // If no session at all, redirect to login with return URL
  if (!session) {
    const currentPath = window.location.pathname;
    const redirectUrl = `/auth?returnTo=${encodeURIComponent(currentPath)}`;
    return <Navigate to={redirectUrl} replace />;
  }

  // If session exists but no profile (likely during session restoration), redirect to login
  if (!profile) {
    const currentPath = window.location.pathname;
    const redirectUrl = `/auth?returnTo=${encodeURIComponent(currentPath)}`;
    return <Navigate to={redirectUrl} replace />;
  }

  // If only authentication is required (no specific role), allow access
  if (requireAuth && !requiredRole) {
    return <>{children}</>;
  }

  // Check if user has the required role - this is a permission issue
  if (requiredRole && effectiveRole !== requiredRole) {
    return <Navigate to="/access-denied" replace />;
  }

  // For superadmin routes, block access if currently impersonating - this is also a permission issue
  if (requiredRole === 'superadmin' && isImpersonating) {
    return <Navigate to="/access-denied" replace />;
  }

  return <>{children}</>;
};
